import React, { useMemo, useCallback } from 'react';
import { Box, HStack, Icon, Text, Menu, MenuButton, MenuList, MenuItem, Divider, useToast } from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { useUserContext } from '../contexts/UserContext';
import TeamLogo from './TeamLogo';
import NotificationMenu from './NotificationMenu';
import ShoppingCartButton from './ShoppingCartButton';
import { getTeamInfoById } from '../utils/getTeamInfo';
import { useMyLeagueStore } from '../store/myLeagueStore';
import { useNavigate } from 'react-router-dom';
import { signOut } from '../utils/signOut';
import { getExpiryCountdown } from '../lib/pstTime';
import { isAdmin } from '../hooks/useUser';

const TopNav: React.FC = () => {
  const { myTeam, leagueAffiliations, switchToTeam } = useUserContext();
  const myLeague = useMyLeagueStore(state => state.myLeague);
  const isAdminFlag = isAdmin();
  const toast = useToast();
  const navigate = useNavigate();

  const teamInfo = myTeam && myLeague ? getTeamInfoById(myTeam, myLeague) : null;

  // Filter out duplicate teams - keep only unique team_id entries
  const uniqueTeams = useMemo(() => {
    if (!leagueAffiliations) return [];
    return leagueAffiliations.filter((affiliation, index, self) => 
      index === self.findIndex(a => Number(a.team_id) === Number(affiliation.team_id))
    );
  }, [leagueAffiliations]);

  // Memoize team info lookups to avoid recalculating on every render
  const teamsWithInfo = useMemo(() => {
    return uniqueTeams.map(affiliation => ({
      ...affiliation,
      teamInfo: getTeamInfoById(Number(affiliation.team_id), myLeague)
    }));
  }, [uniqueTeams, myLeague]);

  const handleTeamSwitch = useCallback(async (teamId: number) => {
    // Find the team info for the toast message
    const teamAffiliation = leagueAffiliations.find(aff => Number(aff.team_id) === teamId);
    
    try {
      await switchToTeam(teamId);
      
      // Show toast notification
      if (teamAffiliation) {
        toast({
          title: `Switched to ${teamAffiliation.team_name} in ${teamAffiliation.league_name} League`,
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      }
    } catch (error) {
      console.error('Error switching team:', error);
      toast({
        title: 'Failed to switch team',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }, [leagueAffiliations, switchToTeam, toast]);

  // Only render when there's a valid myTeam
  if (!myTeam || !myLeague) {
    return null;
  }

  return (
    <Box
      position="fixed"
      top={0}
      right={0}
      zIndex={1000}
      maxW="25vw"
      display={{ base: 'none', md: 'block' }}
    >
      <HStack spacing={3} justify="flex-end" p={3}>
        {/* Shopping Cart Button */}
        <ShoppingCartButton />

        {/* Notification Menu */}
        <NotificationMenu />

        {/* Team Logo Menu Button */}
        <Menu>
          <MenuButton
            as={Box}
            sx={{
              background: 'rgba(0, 46, 11, 0.2)',
            }}
            borderRadius="full"
            p={2}
            borderColor="rgba(255, 255, 255, 0.1)"
            boxShadow="0 4px 6px rgba(0, 0, 0, 0.3)"
            cursor="pointer"
            _hover={{ 
              background: 'rgba(0, 46, 11, 0.3)',
            }}
            transition="background 0.15s"
            position="relative"
          >
            <TeamLogo
              team={{
                name: teamInfo?.name || 'Team',
                logo: teamInfo?.logo,
                color_primary: teamInfo?.color_primary,
                color_secondary: teamInfo?.color_secondary,
                color_text: teamInfo?.color_text
              }}
              size="sm"
            />
        <Box
          position="absolute"
          bottom="0"
          right="0"
          bg="rgba(0, 46, 11, 0.8)"
          borderRadius="full"
          w="16px"
          h="16px"
          display="flex"
          alignItems="center"
          justifyContent="center"
          border="1px solid"
          borderColor="rgba(255, 255, 255, 0.1)"
        >
              <Icon
                as={ChevronDownIcon}
                color="white"
                opacity={0.7}
                w={2}
                h={2}
                transition="transform 0.2s"
              />
            </Box>
          </MenuButton>

          <MenuList
            bg="rgba(0, 0, 0, 0.95)"
            border="1px solid"
            borderColor="rgba(255, 255, 255, 0.2)"
            borderRadius="lg"
            boxShadow="0 8px 32px rgba(0, 0, 0, 0.5)"
            minW="250px"
          >
            {uniqueTeams.length > 1 && (
              <Box px={4} py={2} borderBottom="1px solid" borderColor="rgba(255, 255, 255, 0.1)">
                <Text fontSize="xs" color="gray.400" fontWeight="medium" textTransform="uppercase" letterSpacing="wide" textAlign="left">
                  Switch to:
                </Text>
              </Box>
            )}
            {teamsWithInfo.map(({ teamInfo, ...affiliation }) => (
              <MenuItem
                key={affiliation.team_id}
                onClick={() => handleTeamSwitch(Number(affiliation.team_id))}
                bg="transparent"
                color="white"
                _hover={{ bg: 'rgba(255, 255, 255, 0.1)' }}
                _focus={{ bg: 'rgba(255, 255, 255, 0.1)' }}
                py={3}
                px={4}
              >
                <HStack spacing={3} w="full">
                  <TeamLogo
                    team={{
                      name: affiliation.team_name,
                      logo: teamInfo?.logo,
                      color_primary: teamInfo?.color_primary,
                      color_secondary: teamInfo?.color_secondary,
                      color_text: teamInfo?.color_text
                    }}
                    size="sm"
                  />
                  <Text fontSize="sm" color="white">
                    {affiliation.team_name} {affiliation.league_name} League
                  </Text>
                </HStack>
              </MenuItem>
            ))}
            <Divider borderColor="rgba(255, 255, 255, 0.2)" my={2} />
            <MenuItem
              bg="transparent"
              color="white"
              _hover={{ bg: 'rgba(255, 255, 255, 0.1)' }}
              _focus={{ bg: 'rgba(255, 255, 255, 0.1)' }}
              py={3}
              px={4}
              onClick={() => navigate('/join-league')}
            >
              <Text fontSize="sm" color="white">
                Join A League
              </Text>
            </MenuItem>
            <MenuItem
              bg="transparent"
              color="white"
              _hover={{ bg: 'rgba(255, 255, 255, 0.1)' }}
              _focus={{ bg: 'rgba(255, 255, 255, 0.1)' }}
              py={3}
              px={4}
              onClick={() => navigate('/my-profile')}
            >
              <Text fontSize="sm" color="white">
                My Profile
              </Text>
            </MenuItem>
            <MenuItem
              bg="transparent"
              color="white"
              _hover={{ bg: 'rgba(255, 255, 255, 0.1)' }}
              _focus={{ bg: 'rgba(255, 255, 255, 0.1)' }}
              py={3}
              px={4}
              onClick={() => signOut()}
            >
              <Text fontSize="sm" color="white">
                Sign Out
              </Text>
            </MenuItem>
          </MenuList>
        </Menu>
      </HStack>
    </Box>
  );
};

export default TopNav; 